import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { Plus, Upload, Download, Target, TrendingUp, CheckCircle, Clock, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const KPI = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('goals');
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [newGoal, setNewGoal] = useState({
    employee_id: '',
    title: '',
    description: '',
    target_value: '',
    achieved_value: 0,
    weight: 1.0,
    period: '',
    status: 'in_progress'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, reviewsRes, empRes] = await Promise.all([
        apiClient.get('/api/kpi/goals'),
        apiClient.get('/api/kpi/reviews').catch(() => ({data: []})),
        apiClient.get('/api/employees')
      ]);
      setGoals(goalsRes.data);
      setReviews(reviewsRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/kpi/goals', newGoal);
      fetchData();
      setShowAddGoalModal(false);
      setNewGoal({
        employee_id: '',
        title: '',
        description: '',
        target_value: '',
        achieved_value: 0,
        weight: 1.0,
        period: '',
        status: 'in_progress'
      });
    } catch (error) {
      console.error('Error adding goal:', error);
      alert('Error adding goal');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploadProgress(true);
    setUploadStage('Uploading file...');
    setUploadPercent(10);
    
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setUploadPercent(30);
      setUploadStage('Processing KPI goals...');
      
      await apiClient.post('/api/kpi/goals/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadPercent(80);
      setUploadStage('Finalizing...');
      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadPercent(100);
      
      alert('Goals uploaded successfully!');
      fetchData();
      setShowUploadModal(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploadProgress(false);
      setUploadPercent(0);
      setUploadStage('');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/api/kpi/goals/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kpi_goals_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const handleUpdateProgress = async (goalId, achievedValue) => {
    try {
      await apiClient.put(`/api/kpi/goals/${goalId}`, parseFloat(achievedValue));
      fetchData();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.full_name : empId;
  };

  const calculateProgress = (target, achieved) => {
    if (!target || target === 0) return 0;
    return Math.min((achieved / target) * 100, 100);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>KRA / KPI Management</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>Set goals, track performance, and manage reviews</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUploadModal(true)}
            data-testid="upload-kpi-button"
            className="flex items-center gap-2 bg-[#10B981] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#059669] transition-all"
          >
            <Upload size={20} />
            Upload Excel
          </button>
          <button
            onClick={() => setShowAddGoalModal(true)}
            data-testid="add-goal-button"
            className="flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] transition-all"
          >
            <Plus size={20} />
            Add Goal
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E5E5E5] mb-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('goals')}
            className={`pb-3 px-2 font-medium transition-colors relative ${
              activeTab === 'goals'
                ? 'text-[#7EC845] border-b-2 border-[#7EC845]'
                : 'text-[#737373] hover:text-[#262626]'
            }`}
          >
            Goals & KRAs
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`pb-3 px-2 font-medium transition-colors relative ${
              activeTab === 'reviews'
                ? 'text-[#7EC845] border-b-2 border-[#7EC845]'
                : 'text-[#737373] hover:text-[#262626]'
            }`}
          >
            Performance Reviews
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 px-2 font-medium transition-colors relative ${
              activeTab === 'analytics'
                ? 'text-[#7EC845] border-b-2 border-[#7EC845]'
                : 'text-[#737373] hover:text-[#262626]'
            }`}
          >
            Analytics
          </button>
        </div>
      </div>

      {/* Goals Tab */}
      {activeTab === 'goals' && (
        <div>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6">
              <div className="flex items-center justify-between mb-2">
                <Target className="text-[#7EC845]" size={24} />
                <span className="text-2xl font-bold text-[#262626]">{goals.length}</span>
              </div>
              <p className="text-sm text-[#737373]">Total Goals</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6">
              <div className="flex items-center justify-between mb-2">
                <Clock className="text-[#F59E0B]" size={24} />
                <span className="text-2xl font-bold text-[#262626]">
                  {goals.filter(g => g.status === 'in_progress').length}
                </span>
              </div>
              <p className="text-sm text-[#737373]">In Progress</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="text-[#10B981]" size={24} />
                <span className="text-2xl font-bold text-[#262626]">
                  {goals.filter(g => g.status === 'completed').length}
                </span>
              </div>
              <p className="text-sm text-[#737373]">Completed</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="text-[#1B5E4A]" size={24} />
                <span className="text-2xl font-bold text-[#262626]">
                  {goals.length > 0 ? Math.round(goals.reduce((sum, g) => sum + calculateProgress(g.target_value, g.achieved_value), 0) / goals.length) : 0}%
                </span>
              </div>
              <p className="text-sm text-[#737373]">Avg Progress</p>
            </div>
          </div>

          {/* Goals List */}
          <div className="bg-white rounded-xl border border-[#E5E5E5]">
            <div className="p-6 border-b border-[#E5E5E5]">
              <h3 className="text-lg font-semibold text-[#262626]">Active Goals</h3>
            </div>
            <div className="p-6 space-y-4">
              {goals.length === 0 ? (
                <div className="text-center py-12 text-[#737373]">
                  <Target size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No goals set yet. Add a goal or upload goals via Excel.</p>
                </div>
              ) : (
                goals.map((goal) => {
                  const progress = calculateProgress(goal.target_value, goal.achieved_value);
                  return (
                    <div key={goal.id} className="border border-[#E5E5E5] rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#262626] mb-1">{goal.title}</h4>
                          <p className="text-sm text-[#737373] mb-2">{goal.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-[#737373]">
                              <strong>Employee:</strong> {getEmployeeName(goal.employee_id)}
                            </span>
                            <span className="text-[#737373]">
                              <strong>Period:</strong> {goal.period}
                            </span>
                            <span className="text-[#737373]">
                              <strong>Weight:</strong> {goal.weight}x
                            </span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                          {goal.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#737373]">Progress: {goal.achieved_value} / {goal.target_value}</span>
                          <span className="font-semibold text-[#262626]">{progress.toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-[#F5F5F5] rounded-full h-2.5">
                          <div
                            className="bg-[#7EC845] h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>

                      {user?.role !== 'employee' && goal.status === 'in_progress' && (
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="Update achieved value"
                            className="flex-1 px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateProgress(goal.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                          <button
                            onClick={(e) => {
                              const input = e.target.previousSibling;
                              handleUpdateProgress(goal.id, input.value);
                              input.value = '';
                            }}
                            className="px-4 py-2 bg-[#7EC845] text-white rounded-lg text-sm hover:bg-[#6EB835]"
                          >
                            Update
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div className="bg-white rounded-xl border border-[#E5E5E5] p-6">
          <h3 className="text-lg font-semibold text-[#262626] mb-4">Performance Reviews</h3>
          {reviews.length === 0 ? (
            <div className="text-center py-12 text-[#737373]">
              <p>No performance reviews conducted yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="border border-[#E5E5E5] rounded-lg p-4">
                  <p>Review for {getEmployeeName(review.employee_id)} - Period: {review.period}</p>
                  <p className="text-sm text-[#737373] mt-2">Score: {review.final_score}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="bg-white rounded-xl border border-[#E5E5E5] p-6">
          <h3 className="text-lg font-semibold text-[#262626] mb-4">Performance Analytics</h3>
          <div className="text-center py-12 text-[#737373]">
            <TrendingUp size={48} className="mx-auto mb-4 opacity-50" />
            <p>Analytics dashboard coming soon</p>
          </div>
        </div>
      )}

      {/* Add Goal Modal */}
      {showAddGoalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddGoalModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-[#262626] mb-6">Add New Goal</h2>
            <form onSubmit={handleAddGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Employee*</label>
                <select
                  required
                  value={newGoal.employee_id}
                  onChange={(e) => setNewGoal({...newGoal, employee_id: e.target.value})}
                  data-testid="goal-employee-select"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.emp_code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Goal Title*</label>
                <input
                  type="text"
                  required
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  data-testid="goal-title-input"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                  placeholder="e.g., Sales Target Q1 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Description</label>
                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                  placeholder="Detailed description of the goal"
                ></textarea>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Target Value*</label>
                  <input
                    type="number"
                    required
                    value={newGoal.target_value}
                    onChange={(e) => setNewGoal({...newGoal, target_value: e.target.value})}
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Weight</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newGoal.weight}
                    onChange={(e) => setNewGoal({...newGoal, weight: e.target.value})}
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Period*</label>
                  <input
                    type="text"
                    required
                    value={newGoal.period}
                    onChange={(e) => setNewGoal({...newGoal, period: e.target.value})}
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                    placeholder="2025-Q1"
                  />
                </div>
              </div>
              <div className="flex gap-4 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddGoalModal(false)}
                  className="px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-new-goal"
                  className="px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835]"
                >
                  Add Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full m-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-[#262626] mb-6">Bulk Upload KPI Goals</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-[#737373] mb-4">
                  Upload an Excel file with KPI goals. Download the template to see the required format.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 border border-[#7EC845] text-[#7EC845] rounded-lg hover:bg-[#7EC845]/10 mb-4"
                >
                  <Download size={18} />
                  Download Template
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Select Excel File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg"
                />
                {selectedFile && (
                  <p className="text-sm text-[#737373] mt-2">
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>
              <div className="flex gap-4 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  disabled={uploadProgress}
                  className="px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!selectedFile || uploadProgress}
                  className="px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835] disabled:opacity-50"
                >
                  {uploadProgress ? 'Uploading...' : 'Upload'}
                </button>
              </div>
              
              {uploadProgress && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">{uploadStage}</span>
                    <span className="text-sm font-bold text-blue-900">{uploadPercent}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3">
                    <div
                      className="bg-[#7EC845] h-3 rounded-full transition-all duration-500"
                      style={{ width: `${uploadPercent}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default KPI;